import { Injectable, NgZone, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  environment,
  LogService,
  ProgressService,
  WindowService,
  EventBusService,
  MyHttpClientFactory,
  ResponseCasingEnum,
  LocalStorageService,
  OfflineStorageService,
  NetworkService,
} from '@ups/xplat/core';
import {
  IDynamicModel,
  IDynamicRegisterControl,
  IDynamicModelGroup,
  IDynamicModelProperties,
  IDynamicResponseBody,
  IDynamicControlResponse,
  IDynamicContainerMetadata,
  IDynamicBuilderState,
  IDynamicControl,
  IFormReview,
  IDynamicPageOptions,
  ICannedResponse,
  DynamicModelType,
  IDynamicActiveGroupSettings,
  IDynamicTag,
  IDynamicCategory,
  IDynamicActiveActionOptions,
  IDynamicDatePicker,
  IDynamicTimePicker,
  IDynamicTagDisplay,
  IDynamicConflictBroadcast,
  IDynamicConflictState,
  ITypeaheadSearchFailure,
} from '../models';
import { TranslateService } from '@ngx-translate/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { tagCleanser } from '@ups/xplat/utils';
import { Observable, Subject } from 'rxjs';
import { DynamicAttachmentState } from './dynamic-attachment-state';
import {
  ApplicationModel,
  FileDto,
  FormRole,
  IFileData,
} from '@ups/xplat/api/dto';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AttachmentVpService, CompanyResolver } from '@ups/xplat/api/services';
import { SpartaFileService } from '@ups/files';
import { IMenuService, MENU_SERVICE_TOKEN } from '@ups/common';
import {
  DynamicEventBusTypes,
  emitScopedFormReset,
  DYNAMIC_RETRY_LOOKUPS_EVENT,
  DynamicFreshControlState,
  dynamicSaveIncomplete,
} from '../utils';
import { DynamicContainerListResponse } from './container-list-source';
import { DynamicOfflineCache } from './dynamic-offline-cache';
import { runWhenIdle } from './offline-prefetch.utils';
import { buildDynamicAppMenu } from './dynamic-app-menu';
import {
  addDynamicAppTags,
  addDynamicCategoryTags,
  dynamicContainerListRequest,
  applyDynamicContainerLists,
} from './dynamic-container-list';
import { DynamicRenderApi } from './dynamic-render-api';
import {
  applyDynamicResponseContent,
  buildDynamicDisplayControls,
  dynamicFormPath,
  prepareDynamicResponseValue,
} from './dynamic-display-controls';
import {
  buildDynamicConfigGroup,
  clearDynamicControlSelection,
  IDynamicConfigGroupOptions,
  registerDynamicControl,
} from './dynamic-form-config';
import {
  dynamicActiveFormInvalidMessage,
  dynamicTypeaheadSearchFailures,
  reportDynamicFormErrors,
} from './dynamic-form-validation';
import { DynamicConflictRegistry } from './dynamic-conflict-registry';
import {
  createAndUploadDynamicFile,
  forEachDynamicResponseAttachment,
  getDynamicFile,
  prepareDynamicSignatureForSubmit,
} from './dynamic-render-files';

// various debug logs which can be isolated
const logTag = 'DynamicRenderService';

interface ISubmitNestedControlEvent {
  stopPropagation(): void;
  preventDefault(): void;
}

/**
 * The dynamic-render state and entry points every dynamic page and control shares. WI 31925 split the work into
 * sibling modules (app menu, container list, API calls, display controls, form config and validation, conflict
 * registry); this class keeps the state and the public surface, and its members delegate to them.
 */
@Injectable({
  providedIn: 'root',
})
export class DynamicRenderService {
  metadata!: IDynamicContainerMetadata;
  // active dynamic group
  activeModelGroup!: IDynamicModelGroup;
  // active group settings (these can determine any number of custom ux for dynamic groups)
  activeGroupSettings: IDynamicActiveGroupSettings | null = null;
  activeForm!: UntypedFormGroup;
  activeFormProperties!: UntypedFormGroup;
  isFormDirty = false;
  readonly freshControlState = new DynamicFreshControlState();
  itemTargetUpdate$: Subject<IDynamicModel> = new Subject();
  /**
   * Conflict-resolution message bus (mirrors itemTargetUpdate$). The service holds NO long-lived
   * conflict state — it only fans messages out. State lives on the components (target typeahead +
   * labels) and dies with them. This matters because in Mode A (embedded controls, e.g. the SRF
   * modal) resetActiveForm() is never called; component lifecycle is the only reliable teardown.
   */
  conflictUpdate$: Subject<IDynamicConflictBroadcast> = new Subject();
  private readonly conflicts = new DynamicConflictRegistry(
    () => this.conflictUpdate$
  );
  // file field attachments + the active response; every write emits changed$ (WI 35584)
  readonly attachmentState = new DynamicAttachmentState();
  get activeFormResponse() {
    return this.attachmentState.formResponse;
  }
  set activeFormResponse(value: IDynamicResponseBody | null) {
    this.attachmentState.formResponse = value;
  }
  get activeAttachments() {
    return this.attachmentState.attachments;
  }
  set activeAttachments(value: { [key: string]: Array<FileDto> } | null) {
    this.attachmentState.attachments = value;
  }
  activeAction?: (options?: IDynamicActiveActionOptions) => Promise<unknown>;
  activeActionDone$: Subject<unknown> = new Subject();
  toggleDatePicker$: Subject<IDynamicDatePicker> = new Subject();
  toggleTimePicker$: Subject<IDynamicTimePicker> = new Subject();
  formReviewDone$: Subject<void> = new Subject();
  formReady$: Subject<void> = new Subject();
  dynamicContainerList!: Array<IDynamicContainerMetadata>;
  dynamicContainerListAll!: Array<IDynamicContainerMetadata>;
  dynamicContainerListAllVersions!: Array<IDynamicContainerMetadata>;
  dynamicAppTags!: Array<IDynamicTagDisplay>;
  dynamicCategories!: Array<IDynamicTagDisplay>;
  showingAllVersions = false;
  // helps form builder UX
  formBuilderState!: IDynamicBuilderState;
  companyResolver = inject(CompanyResolver);
  selectedControl!: IDynamicModel;
  // signature components override the following to determine if user has physically signed the components
  isSignatureEmpty!: () => boolean;
  signatureUploadFile: FileDto | null = null;
  signatureBase64!: () => Promise<string>;
  signatureReset?: () => boolean;
  _selectNestedControl$?: Subject<IDynamicModel>;

  // Bug 34694: in-flight loads shared by overlapping callers (AppDynamicGuard
  // runs once per pending navigation), keyed by the feed each one reads.
  private _containerListLoads = new Map<
    string,
    Promise<Array<IDynamicContainerMetadata>>
  >();
  private readonly translate = inject(TranslateService);
  private readonly progress = inject(ProgressService);
  private readonly clientFactory = inject(MyHttpClientFactory);
  private readonly menuService = inject(MENU_SERVICE_TOKEN, {
    optional: true,
  }) as IMenuService | null;
  public log = inject(LogService);
  public win = inject(WindowService);
  public ngZone = inject(NgZone);
  public eventBus = inject(EventBusService);
  public storage = inject(LocalStorageService);
  public network = inject(NetworkService);
  public offlineStorage = inject(OfflineStorageService);
  private readonly offlineCache = new DynamicOfflineCache({
    http: () => this.http,
    offlineStorage: () => this.offlineStorage,
    log: () => this.log,
    win: () => this.win,
  });
  private readonly api = new DynamicRenderApi({
    http: () => this.http,
    log: () => this.log,
  });
  public formBuilder = inject(UntypedFormBuilder);
  public attachmentService = inject(AttachmentVpService);
  public spartaFileService = inject(SpartaFileService);
  public appModel = inject(ApplicationModel);
  private readonly http: HttpClient = this.clientFactory.createHttpClient(
    this.getRequiredUrl(environment.urls?.dynamicAPI, 'dynamicAPI'),
    true,
    ResponseCasingEnum.PascalCase
  );

  get selectNestedControl$() {
    if (!this._selectNestedControl$) {
      this._selectNestedControl$ = new Subject();
    }
    return this._selectNestedControl$;
  }

  // convenient to avoid having to inject translateService into various dynamic components
  translateKey(key: string) {
    return this.translate.instant(key);
  }

  /**
   * Update an apps menu with listing of dynamic containers combined with the apps menu
   * @returns The first dynamic container in the list (if any)
   */
  updateAppMenu(menuItemDefaultPerms: Array<string | boolean>) {
    const menuService = this.menuService;

    if (!menuService) {
      return;
    }

    this.ngZone.run(() => {
      const menuConfigItems = buildDynamicAppMenu({
        containers: this.dynamicContainerList,
        categories: this.dynamicCategories,
        menuItemDefaultPerms,
        isBrowser: this.win.isBrowser,
        staticMenuConfig: menuService.staticMenuConfig,
      });
      menuService.configureMenu(menuConfigItems, 0, this.win.isMobile);
    });
  }

  loadAllVersionContainerList(
    forceRefresh?: boolean
  ): Promise<Array<IDynamicContainerMetadata>> {
    if (
      forceRefresh ||
      !this.dynamicContainerListAllVersions ||
      !this.dynamicContainerListAllVersions.length
    ) {
      return this.api.loadAllVersions(
        () => this.getAppTagName(),
        (list) => (this.dynamicContainerListAllVersions = list)
      );
    }
    return Promise.resolve(this.dynamicContainerListAllVersions);
  }

  loadFormRoles(): Promise<Array<FormRole>> {
    return this.api.loadFormRoles();
  }

  loadContainerList(
    localCategories: Array<IDynamicCategory>,
    menuItemDefaultPerms: Array<string | boolean>, // a ViewFeatureExpression list, e.g. [true]
    taggedWithAppOnly = true,
    showAllVersions = false,
    forceRefresh?: boolean,
    publishedOnly = false // WI 30576: prod nav reads the published-only feed
  ): Promise<Array<IDynamicContainerMetadata>> {
    // Bug 34694: dynamicContainerList is only set once the response lands, so
    // overlapping callers used to each load (and each start the offline
    // prefetch). They now share the one in-flight load of the same feed.
    const loadKey = `${taggedWithAppOnly}:${showAllVersions}:${publishedOnly}`;
    const inFlight = this._containerListLoads.get(loadKey);
    if (inFlight && !forceRefresh) {
      return inFlight;
    }
    const load = new Promise<Array<IDynamicContainerMetadata>>((resolve) => {
      this.showingAllVersions = showAllVersions;
      if (forceRefresh || !this.dynamicContainerList) {
        const appTag = this.getAppTagName();
        const { usingAll, httpUrl, httpParams } = dynamicContainerListRequest(
          appTag,
          taggedWithAppOnly,
          this.showingAllVersions,
          publishedOnly
        );
        // PR 4289 review: settle in finally, so a throw below cannot leave a
        // never-resolving load in _containerListLoads (the error still reaches rxjs).
        this.api
          .loadContainerList(
            usingAll,
            httpUrl,
            httpParams,
            appTag,
            publishedOnly
          )
          .subscribe((list: DynamicContainerListResponse) => {
            try {
              this.applyContainerList(
                list,
                localCategories,
                menuItemDefaultPerms,
                usingAll,
                forceRefresh
              );
            } finally {
              resolve(this.dynamicContainerList);
            }
          });
      } else {
        resolve(this.dynamicContainerList);
      }
    });
    this._containerListLoads.set(loadKey, load);
    load.then(() => {
      if (this._containerListLoads.get(loadKey) === load) {
        this._containerListLoads.delete(loadKey);
      }
    });
    return load;
  }

  /** Seam for Bug 34694 tests: defers background offline caching to idle. */
  protected scheduleOfflinePrefetch(task: () => void): void {
    runWhenIdle(task);
  }

  setupAppTags(tags: Array<IDynamicTag>) {
    addDynamicAppTags(this.dynamicAppTags, tags);
  }

  setupCategoryTags(
    tags: Array<IDynamicTag>,
    localCategories: Array<IDynamicCategory>
  ) {
    addDynamicCategoryTags(this.dynamicCategories, tags, localCategories);
  }

  loadContainersIncludingControls(
    tagNames: string[] | null = null
  ): Promise<IDynamicContainerMetadata[]> {
    return this.api.loadContainersIncludingControls(tagNames);
  }

  loadContainerDetails(
    metadata: IDynamicContainerMetadata
  ): Promise<IDynamicContainerMetadata> {
    return this.api.loadContainerDetails(metadata, (container) =>
      this.updateMetaData(container)
    );
  }

  loadReusableControls(): Promise<Array<IDynamicControl>> {
    return this.api.loadReusableControls();
  }

  loadReportCategories(type?: DynamicModelType): Promise<Array<string>> {
    return this.api.loadReportCategories(type);
  }

  loadContainerResponse(
    dynamicResponseId: string
  ): Promise<IDynamicResponseBody> {
    return this.api.loadContainerResponse(dynamicResponseId);
  }

  applyResponseContent(
    control: IDynamicControl,
    dynamicResponse: IDynamicResponseBody,
    options?: IDynamicPageOptions
  ) {
    applyDynamicResponseContent(this, control, dynamicResponse, options);
  }

  prepareResponseValue(response?: IDynamicControlResponse | null) {
    return prepareDynamicResponseValue(response);
  }

  prepareContainerForDisplay(
    container: Partial<IDynamicContainerMetadata>,
    isFormBuilder?: boolean,
    options?: IDynamicPageOptions
  ): Promise<Array<IDynamicModel>> {
    return new Promise((resolve) => {
      const prepare = (dynamicResponse?: IDynamicResponseBody) => {
        resolve(
          buildDynamicDisplayControls(
            this,
            container,
            isFormBuilder,
            options,
            () =>
              options ? this.getFormPath(options) || undefined : undefined,
            dynamicResponse
          )
        );
      };

      this.activeFormResponse = null;

      const prepareResponse = (response: IDynamicResponseBody) => {
        this.activeFormResponse = options?.isReclassification ? null : response;
        this.checkResponseForAttachments(response);
        prepare(response);
      };
      if (options?.dynamicResponseId) {
        this.loadContainerResponse(options?.dynamicResponseId).then((r) => {
          prepareResponse(r);
        });
      } else if (options?.dynamicResponseBody) {
        prepareResponse(options.dynamicResponseBody);
      } else {
        prepare();
      }
    });
  }

  /**
   * Everytime a dynamic container is displayed, each control will be passed through this
   * Can be used to auto-update property name refactors from older legacy naming to latest
   * After >90 days of new versions being released with migration handling in place, can safely remove those migrations applied >90 days ago
   * @param control dynamic control
   */
  checkResponseForAttachments(response?: IDynamicResponseBody | null) {
    const activeResponse = response || this.activeFormResponse;

    if (activeResponse?.dynamicControlResponses) {
      this.activeAttachments = {};
      forEachDynamicResponseAttachment(activeResponse, (name, file) =>
        this.addAttachmentForField(name, file)
      );
    }
  }

  getAppTagName(selectedAppTag?: string) {
    const name = tagCleanser(selectedAppTag || environment.name || '');
    // Note: could run migration to add `app:` prefix to tags which don't have a prefix
    // Started adding `app:` prefix to tags in October 2023
    // TODO: Talk with Peter/Tim about using multiple tags with tagNames param?
    // For awhile (without a migration), we need to request 'safety,app:safety'
    return name === 'safety' ? name : encodeURIComponent(`app:${name}`);
  }

  getFormPath(options: IDynamicPageOptions): string | null {
    return dynamicFormPath(
      this.dynamicContainerList,
      this.dynamicContainerListAll,
      options
    );
  }

  updateMetaData(value: IDynamicContainerMetadata) {
    this.metadata = {
      ...(this.metadata || {}),
      ...value,
    };
  }

  resetMetaData() {
    this.updateMetaData({
      dynamicContainerName: '',
      dynamicContainerDescription: '',
      dynamicContainerId: '',
      displayIcon: '',
      tags: [],
      dynamicContainerWhatsNew: '',
      isDirty: false,
    });
  }

  updateFormBuilderState(value: IDynamicBuilderState) {
    this.formBuilderState = {
      ...(this.formBuilderState || {}),
      ...value,
    };
  }

  selectNestedControl(
    e: ISubmitNestedControlEvent | null | undefined,
    itemConfig: IDynamicModel | null | undefined
  ): false | undefined {
    if (e && itemConfig && itemConfig.isFormBuilder) {
      e.stopPropagation();
      e.preventDefault();
      this.selectNestedControl$.next(itemConfig);
      return false;
    }

    return undefined;
  }

  submitForm(form: UntypedFormGroup, publish?: boolean, draft?: boolean) {
    this.log.debug(logTag, 'form:', form.value);
    this.log.debug(logTag, 'isFormValid:', this.isFormValid(form));
    if (this.activeAction) {
      this.activeAction({
        publish,
        draft,
      }).then(
        (result) => {
          this._resetSpinner();
          if (dynamicSaveIncomplete(result)) return; // #34974 + WI 34982: keep the host; a resubmit reuses the saved file IDs + signature
          this.activeActionDone$.next(result);
          this.activeAttachments = null;
          this.signatureUploadFile = null;
        },
        (errorMessage) => {
          this.win.alert(errorMessage);
          this._resetSpinner();
        }
      );
    }
  }

  isFormValid(form: UntypedFormGroup): boolean {
    if (form) {
      if (form.status === 'INVALID' && !form.valid) {
        this.isFormDirty = true;
        reportDynamicFormErrors(form, this.win);
        return false;
      }
    }
    return true;
  }

  /** Typeahead controls whose remote search failed (ADO #30153); see dynamicTypeaheadSearchFailures. */
  getTypeaheadSearchFailures(
    form: UntypedFormGroup | null | undefined,
    controls?: Array<IDynamicModel | IDynamicModelProperties> | null
  ): Array<ITypeaheadSearchFailure> {
    return dynamicTypeaheadSearchFailures(form, controls);
  }

  /**
   * Non-blocking submitter notice when any typeahead had a degraded remote search.
   * Does not prevent submit. Resolves after the user dismisses (or immediately if none).
   */
  notifyTypeaheadSearchFailures(
    failures: Array<ITypeaheadSearchFailure>
  ): Promise<void> {
    if (!failures?.length) {
      return Promise.resolve();
    }
    const names = failures
      .map((f) => f.label || f.formControlName)
      .filter(Boolean)
      .join(', ');
    return this.win
      .alert({
        title: 'Some lookups did not complete',
        message: `The following fields could not load search results: ${names}. You can still submit. Empty values on those fields may be incomplete.`,
        okButtonText: 'OK',
      })
      .then(() => undefined)
      .catch(() => undefined);
  }

  createConfigGroup(options: IDynamicConfigGroupOptions): IDynamicModelGroup {
    const modelGroup = buildDynamicConfigGroup(options, this.log);
    if (options.activateModelGroup) {
      this.activeModelGroup = modelGroup;
      this.activeGroupSettings = null;
    }
    return modelGroup;
  }

  updateActiveGroupSettings(updates: IDynamicActiveGroupSettings) {
    this.activeGroupSettings = {
      ...(this.activeGroupSettings || {}),
      ...updates,
    };
  }

  /** WI 35277: ask every typeahead whose saved-value lookup failed to run it again. */
  retryFailedLookups(): void {
    this.eventBus.emit(DYNAMIC_RETRY_LOOKUPS_EVENT);
  }

  registerControlForGroup(
    config: IDynamicModel,
    group: UntypedFormGroup
  ): IDynamicRegisterControl {
    return registerDynamicControl(config, group, this.freshControlState);
  }

  // Conflict resolution: see DynamicConflictRegistry.

  /** A source control declares it pushes into `targetName` (from its updateOnValueChange). */
  registerConflictSource(
    sourceName: string,
    targetName: string,
    sourceLabel?: string
  ): void {
    this.conflicts.register(sourceName, targetName, sourceLabel);
  }

  /** Remove a source mapping (called on the source control's destroy so closed forms leave nothing stale). */
  deregisterConflictSource(sourceName: string, targetName: string): void {
    this.conflicts.deregister(sourceName, targetName);
  }

  /** Registered sources for a target with their labels, for the conflict popover. */
  conflictSourceMetaFor(
    targetName: string
  ): Array<{ formControlName: string; label?: string }> {
    return this.conflicts.sourceMetaFor(targetName);
  }

  /** Raise a conflict: notify the target label and every contributing source label. */
  raiseConflict(state: IDynamicConflictState): void {
    this.conflicts.raise(state);
  }

  /** User chose how to resolve a conflict on `targetName`. Fans out to target + sources. */
  resolveConflict(targetName: string, choice: 'default' | 'keep'): void {
    this.conflicts.resolve(targetName, choice);
  }

  /** Clear any conflict state for `targetName` (target + sources). */
  clearConflict(targetName: string): void {
    this.conflicts.clear(targetName);
  }

  createAndUploadFileForConfig(
    filename: string,
    content: string,
    contentType: string,
    config: IDynamicModel
  ): Promise<FileDto | null> {
    return createAndUploadDynamicFile(
      this,
      filename,
      content,
      contentType,
      config
    );
  }

  addAttachmentForField(name: string, attachment: FileDto) {
    this.attachmentState.add(name, attachment);
  }

  removeAttachmentForField(name: string, attachment: FileDto) {
    this.attachmentState.remove(name, attachment);
  }

  getFile(
    attachmentID: string,
    useSparta?: boolean,
    spartaDownload?: boolean
  ): Promise<string | IFileData | null> {
    return getDynamicFile(this, attachmentID, useSparta, spartaDownload);
  }

  isExistingContainer() {
    return !!(this.metadata && this.metadata.dynamicContainerId);
  }

  isDirtyContainer() {
    return !!(this.metadata && this.metadata.isDirty);
  }

  saveDynamicContainer(body: IDynamicContainerMetadata) {
    if (this.isExistingContainer()) {
      body.dynamicContainerId = this.metadata.dynamicContainerId;
      return this.api.saveContainer(body, this.metadata.dynamicContainerId);
    }
    return this.api.saveContainer(body);
  }

  publishDynamicContainer(containerId?: string, version?: number) {
    return this.api.publishContainer(containerId, version);
  }

  /**
   * Ability to prepare any extracurricular data before submission
   * For example: attachment handling with dynamic signature components
   * This method can be used to expand on any type of extra preprocessing on the data before submitting user response
   * @param controls collection of dynamic models
   * @returns
   */
  prepareToSubmitResponse(controls: Array<IDynamicModel>): Promise<void> {
    return prepareDynamicSignatureForSubmit(this, this.progress, controls);
  }

  saveDynamicResponse(
    dynamicControlResponses?: Array<IDynamicControlResponse>,
    draft?: boolean,
    typeaheadSearchFailures?: Array<ITypeaheadSearchFailure>
  ): Observable<IDynamicResponseBody | null> {
    const body: IDynamicResponseBody = {
      dynamicContainerResponseId:
        this.activeFormResponse?.dynamicContainerResponseId,
      dynamicContainerId: this.metadata.dynamicContainerId,
      isActive: true,
      isDraft: false,
      isFinal: true,
      dynamicControlResponses,
    };

    if (draft) {
      body.isDraft = true;
    }

    if (typeaheadSearchFailures?.length) {
      body.typeaheadSearchFailures = typeaheadSearchFailures;
      this.log.debug(
        logTag,
        'typeaheadSearchFailures at submit:',
        typeaheadSearchFailures
      );
    }

    return this.api.saveResponse(body);
  }

  updateDynamicResponse(deleteComment: string) {
    return this.api.updateResponse({
      dynamicContainerResponseId:
        this.activeFormResponse?.dynamicContainerResponseId,
      dynamicContainerId: this.metadata.dynamicContainerId,
      isActive: false,
      deleteComment,
    });
  }

  failValidation(message = 'Please fill out all the required fields.') {
    this.progress.toggleSpinner(false);
    // Always show the caller's message: form-level errors on activeForm used to swallow it,
    // leaving a failed Submit with no feedback (Bug 34679).
    this.win.setTimeout(() => this.win.alert(message));
  }

  /**
   * Checks the activeForm [FormGroup] and any nested FormGroup validity
   * @param modelGroup (optional) validate against a specific IDynamicModelGroup vs. just the activeModelGroup
   * @param skipNestedFormGroups (optional) skip validating nested form groups. useful
   * @returns failure message if form is invalid
   */
  isActiveFormValid(
    modelGroup?: IDynamicModelGroup | null | undefined,
    skipNestedFormGroups?: boolean
  ): string | null {
    return dynamicActiveFormInvalidMessage(
      this.activeForm,
      modelGroup || this.activeModelGroup,
      skipNestedFormGroups
    );
  }

  /**
   * Prompts a confirm dialog before resetting
   * @returns Promise when confirmed to reset
   */
  resetActiveForm(): Promise<void> {
    const discardTarget = this.getActiveDiscardTargetName();
    return new Promise((resolve) => {
      this.win
        .confirm({
          title: discardTarget,
          message: `Are you sure you want to cancel and discard your changes to ${discardTarget}?`,
          okButtonText: 'Discard Changes',
          cancelButtonText: 'Keep Editing',
        })
        .then((ok) => {
          if (ok) {
            this.clearActiveForm();
            resolve();
          }
        });
    });
  }

  confirmSaveActiveFormAsDraft(): Promise<void> {
    const discardTarget = this.getActiveDiscardTargetName();
    const message =
      'You have unsaved changes. Choose Save Draft to keep your progress, or Discard Changes to leave without saving.';
    return new Promise((resolve) => {
      this.win
        .confirm({
          title: discardTarget,
          message,
          okButtonText: 'Save Draft',
          cancelButtonText: 'Discard Changes',
        })
        .then((ok) => {
          if (ok) {
            this.submitForm(this.activeForm, undefined, true);
          } else {
            this.clearActiveForm();
          }
          resolve();
        });
    });
  }

  /**
   * Immediately clears and resets the active form without prompting to confirm
   */
  clearActiveForm(ignoreFormGroupReset?: boolean) {
    if (!ignoreFormGroupReset) {
      // ignoreFormGroupReset can be used when just recreating the FormGroup [activeForm], no need to trigger reset events, however still want to clear all the service level state associated with activeForm's
      if (this.activeForm) {
        // restores each control's fresh-open enabled/hidden state, then resets values (#34303)
        this.freshControlState.reset(this.activeForm);
      }
      // WI 32220: resets only the controls rendered in the form just cleared
      emitScopedFormReset(this.eventBus, this.activeForm);
    }
    // clear any other service level state associated with forms
    this.activeAttachments = {};
    this.activeFormResponse = null;
    if (this.signatureReset) {
      this.signatureReset();
    }
  }

  private getActiveDiscardTargetName() {
    return this.metadata?.dynamicContainerName || 'Form';
  }

  /**
   * Resets various state level properties within dynamic controls
   * ie, selected state
   * @param controls dynamic models
   */
  resetControlState(controls: IDynamicModel[], scope?: AbstractControl) {
    clearDynamicControlSelection(controls);
    // WI 32220: scoped to one form, or an explicit GLOBAL reset (NS Safety filters)
    const payload = scope ? { scope } : true;
    this.eventBus.emit(DynamicEventBusTypes.dynamicFormResetState, payload);
  }

  cacheDynamicContainersForOffline(data: Array<IDynamicContainerMetadata>) {
    return this.offlineCache.cacheContainers(data);
  }

  /**
   * Prefetches dropdown data and stores for offline use later
   * @returns Promise when prefetch and cache is complete
   */
  prefetchDropdownsForOffline(forceRefresh?: boolean) {
    return this.offlineCache.prefetchDropdowns(forceRefresh);
  }

  saveFormReview(dto: IFormReview) {
    return this.api.saveFormReview(dto);
  }

  getFormReview(
    dynamicResponseId: string,
    type: string = ''
  ): Observable<IFormReview> {
    return this.api.getFormReview(dynamicResponseId, type);
  }

  getCannedResponses(type?: string): Observable<ICannedResponse[]> {
    return this.api.getCannedResponses(type);
  }

  aiCoach(
    dynamicContainerResponseId: string,
    createReviewRecord: boolean = false
  ): Observable<IFormReview> {
    return this.api.aiCoach(dynamicContainerResponseId, createReviewRecord);
  }

  /** Stores a loaded container list, rebuilds the menu, and schedules the offline prefetch (loadContainerList). */
  private applyContainerList(
    list: DynamicContainerListResponse,
    localCategories: Array<IDynamicCategory>,
    menuItemDefaultPerms: Array<string | boolean>,
    usingAll: boolean,
    forceRefresh?: boolean
  ) {
    if (list) {
      applyDynamicContainerLists(this, list, localCategories, this.log);
    } else {
      this.win.alert(
        `The dynamic data api is not warmed up yet. ${
          this.win.isBrowser
            ? 'You may try refreshing the browser shortly.'
            : 'Please quit the app and try restarting it in a moment.'
        }`
      );
    }
    this.updateAppMenu(menuItemDefaultPerms);
    if (usingAll && (!this.offlineCache.hasCachedData || forceRefresh)) {
      // Bug 34694: background work, so it waits for idle instead of
      // competing with the navigation this list is gating.
      this.scheduleOfflinePrefetch(() => {
        if (this.win.navigator.onLine) {
          // cache all dynamic containers for offline use later
          this.cacheDynamicContainersForOffline(
            this.dynamicContainerListAll
          ).then(() => {
            this.prefetchDropdownsForOffline(forceRefresh);
          });
        } else {
          this.prefetchDropdownsForOffline();
        }
      });
    }
  }

  private _getValidators(field: IDynamicModel) {
    const validators: ValidatorFn[] = [];
    if (field.required) {
      validators.push(Validators.required);
    }
    return validators;
  }

  private _resetSpinner() {
    this.progress.infinitePreventionOn = true;
    this.progress.toggleSpinner(false);
  }

  private getRequiredUrl(url: string | undefined, key: string): string {
    if (!url) {
      throw new Error(`Missing environment URL: ${key}`);
    }

    return url;
  }
}
